import { supabase } from '../../lib/supabase';
import { ClientProfile } from '../../types';
import { handleServiceError } from '../utils/serviceUtils';
import { createDefaultProfile, mapProfileFromDB, mapProfileToDB } from '../mappers/profileMapper';

/**
 * Busca o perfil do cliente filtrado pela organização
 */
export const getClientProfile = async (organizationId: string, user?: any): Promise<ClientProfile> => {
    try {
        if (!user) {
            const { data: { session } } = await supabase.auth.getSession();
            user = session?.user;
        }

        const defaultProfile = createDefaultProfile(user);

        const { data, error } = await supabase
            .from('business_profiles')
            .select('*')
            .eq('user_id', user.id)
            .maybeSingle();

        if (error) {
            console.error('Error fetching client profile:', error);
            return defaultProfile;
        }

        if (!data) {
            return defaultProfile;
        }

        return mapProfileFromDB(data, defaultProfile);
    } catch (error) {
        console.error('Error fetching client profile:', error);
        const { data: { session } } = await supabase.auth.getSession();
        return createDefaultProfile(session?.user);
    }
};

/**
 * Salva o perfil do cliente com vínculo à organização
 */
export const saveClientProfile = async (profile: ClientProfile, organizationId: string): Promise<void> => {
    try {
        const { data: { session } } = await supabase.auth.getSession();
        const user = session?.user;

        if (!user) {
            throw new Error('Usuário não autenticado');
        }

        if (!organizationId) {
            throw new Error('Organização não identificada');
        }

        // 1. Atualizar metadados do usuário (Nome Pessoal no Auth)
        if (profile.name !== user.user_metadata?.full_name) {
            const { error: authError } = await supabase.auth.updateUser({
                data: { full_name: profile.name }
            });
            if (authError) {
                console.warn('Erro ao atualizar nome do usuário:', authError);
                // Não bloqueamos o fluxo principal
            }

            // Atualizar também a tabela profiles (usada no chat e listagem de usuários)
            const { error: profileError } = await supabase
                .from('profiles')
                .update({ full_name: profile.name })
                .eq('id', user.id);
                
            if (profileError) {
                console.warn('Erro ao atualizar tabela profiles:', profileError);
            }
        }

        // 2. Verificar se já existe um perfil para este usuário
        const { data: existingProfile } = await supabase
            .from('business_profiles')
            .select('user_id')
            .eq('user_id', user.id)
            .maybeSingle();

        const profileData = mapProfileToDB(user, profile, organizationId);

        let error;
        if (existingProfile) {
            // Atualizar existente
            const { error: updateError } = await supabase
                .from('business_profiles')
                .update(profileData)
                .eq('user_id', user.id);
            error = updateError;
        } else {
            // Inserir novo
            const { error: insertError } = await supabase
                .from('business_profiles')
                .insert(profileData);
            error = insertError;
        }

        if (error) throw error;
    } catch (error) {
        handleServiceError('salvar perfil', error);
    }
};
